import { type EditAction } from '@/application';
import { type EntitySource, type Evaluated } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { createEntity, type EntityId } from '@/domain/model';

import { constrainDirection, type SnapResult } from '../Snapping';

import { createConstructionTools } from './constructions';
import { createDrawingTools } from './drawing';
import { createFreePlaneTools } from './freePlane';
import { currentSnap, placePoint } from './pointFactory';
import { createSolidTools } from './solids';
import { TextTool } from './text';
import { type PointerInput, type Tool, type ToolContext, type ToolResponse } from './Tool';
import { TransformTool } from './TransformTool';

/**
 * Creation tools.
 *
 * All of them share one shape: collect points, then emit the entity that joins them.
 * The shared machinery lives in `TwoPointTool` and `PointCollector` rather than being
 * copy-pasted five times, because the interesting part of each tool is one function —
 * what to build from the collected points — and everything else is identical.
 *
 * Every point they collect goes through `placePoint`, so clicking on a circle produces a
 * locus point and clicking on a crossing produces an intersection point. The tools do
 * not know that; they just ask for a point and get whatever the geometry justifies.
 */

/* ------------------------------------------------------------------ point tool */

export class PointTool implements Tool {
  readonly id = 'point';
  readonly label = 'Điểm';
  readonly shortcut = 'p';
  readonly group = 'Vẽ';

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;
    const snapped = currentSnap(context, input);
    const placement = placePoint(context, snapped, input.world);
    return {
      actions: placement.actions,
      selection: [placement.id],
      snap: snapped,
    };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const snapped = currentSnap(context, input);
    return {
      snap: snapped,
      preview: [{ kind: 'point', point: snapped.world }],
      cursor: 'crosshair',
    };
  }

  cancel(): ToolResponse | null {
    return null;
  }

  reset(): void {
    /* stateless */
  }
}

/* -------------------------------------------------------------- two-point tools */

type TwoPointState =
  | { readonly name: 'idle' }
  | { readonly name: 'placed'; readonly first: EntityId; readonly firstWorld: Vec2 };

/**
 * A tool that takes two points and builds one entity.
 *
 * Covers segment, ray, line, vector and circle. `build` receives the two point ids and
 * returns the source to create; `previewOf` draws the in-progress shape.
 */
class TwoPointTool implements Tool {
  private state: TwoPointState = { name: 'idle' };

  // Explicit fields rather than constructor parameter properties: `erasableSyntaxOnly`
  // forbids the shorthand so that every source file is valid type-stripped JavaScript.
  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
  readonly group = 'Vẽ';
  private readonly prefix: string;
  private readonly build: (a: EntityId, b: EntityId) => EntitySource;
  private readonly previewOf: (a: Vec2, b: Vec2) => Evaluated;

  constructor(
    id: string,
    label: string,
    shortcut: string,
    prefix: string,
    build: (a: EntityId, b: EntityId) => EntitySource,
    previewOf: (a: Vec2, b: Vec2) => Evaluated
  ) {
    this.id = id;
    this.label = label;
    this.shortcut = shortcut;
    this.prefix = prefix;
    this.build = build;
    this.previewOf = previewOf;
  }

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;
    const snapped = currentSnap(context, input);

    if (this.state.name === 'idle') {
      const placement = placePoint(context, snapped, input.world);
      this.state = { name: 'placed', first: placement.id, firstWorld: snapped.world };
      return { actions: placement.actions, snap: snapped };
    }

    const world = this.constrain(input, snapped);
    const placement = placePoint(context, snapped, world);

    // A degenerate entity helps nobody; keep waiting for a distinct second point.
    if (placement.id === this.state.first) {
      return { snap: snapped };
    }

    const id = context.nextId(this.prefix);
    const actions: EditAction[] = [
      ...placement.actions,
      {
        kind: 'add-entity',
        entity: createEntity(id, this.build(this.state.first, placement.id), context.style),
      },
    ];

    this.state = { name: 'idle' };
    return { actions, selection: [id], preview: [], snap: snapped };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const snapped = currentSnap(context, input);
    if (this.state.name === 'idle') {
      return {
        snap: snapped,
        preview: [{ kind: 'point', point: snapped.world }],
        cursor: 'crosshair',
      };
    }
    const world = this.constrain(input, snapped);
    return {
      snap: snapped,
      preview: [this.previewOf(this.state.firstWorld, world)],
      cursor: 'crosshair',
    };
  }

  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    return { preview: [] };
  }

  reset(): void {
    this.state = { name: 'idle' };
  }

  private constrain(input: PointerInput, snapped: SnapResult): Vec2 {
    if (this.state.name !== 'placed' || !input.modifiers.shift) return snapped.world;
    return constrainDirection(this.state.firstWorld, input.world);
  }
}

export function createSegmentTool(): Tool {
  return new TwoPointTool(
    'segment',
    'Đoạn thẳng',
    's',
    'seg',
    (a, b) => ({ op: 'segment', a, b }),
    (a, b) => ({ kind: 'segment', segment: { a, b } })
  );
}

export function createRayTool(): Tool {
  return new TwoPointTool(
    'ray',
    'Tia',
    'r',
    'ray',
    (origin, through) => ({ op: 'ray', origin, through }),
    (a, b) => ({
      kind: 'ray',
      ray: { origin: a, direction: vec.normalize(vec.subtract(b, a)) },
    })
  );
}

export function createLineTool(): Tool {
  return new TwoPointTool(
    'line',
    'Đường thẳng',
    'l',
    'ln',
    (a, b) => ({ op: 'line', a, b }),
    (a, b) => ({
      kind: 'line',
      line: { origin: a, direction: vec.normalize(vec.subtract(b, a)) },
    })
  );
}

export function createCircleTool(): Tool {
  return new TwoPointTool(
    'circle',
    'Đường tròn',
    'c',
    'cir',
    (center, through) => ({ op: 'circle-through', center, through }),
    (center, through) => ({
      kind: 'circle',
      circle: { center, radius: vec.distance(center, through) },
    })
  );
}

/* ------------------------------------------------------------- polygon tool */

type PolygonState =
  | { readonly name: 'idle' }
  | {
      readonly name: 'collecting';
      readonly ids: readonly EntityId[];
      readonly points: readonly Vec2[];
    };

/**
 * Collects vertices until the ring is closed.
 *
 * Closing happens by clicking the first vertex again, by double-clicking, or by pressing
 * Enter — three ways because users reach for different ones and a polygon you cannot
 * finish is a trap.
 */
export class PolygonTool implements Tool {
  readonly id = 'polygon';
  readonly label = 'Đa giác';
  readonly shortcut = 'g';
  readonly group = 'Vẽ';

  private state: PolygonState = { name: 'idle' };

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;
    const snapped = currentSnap(context, input);
    const placement = placePoint(context, snapped, input.world);

    if (this.state.name === 'idle') {
      this.state = { name: 'collecting', ids: [placement.id], points: [snapped.world] };
      return { actions: placement.actions, snap: snapped };
    }

    // Clicking the first vertex again closes the ring.
    if (placement.reused && placement.id === this.state.ids[0] && this.state.ids.length >= 3) {
      return this.close(context);
    }

    this.state = {
      name: 'collecting',
      ids: [...this.state.ids, placement.id],
      points: [...this.state.points, snapped.world],
    };
    return { actions: placement.actions, snap: snapped };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const snapped = currentSnap(context, input);
    if (this.state.name === 'idle') {
      return {
        snap: snapped,
        preview: [{ kind: 'point', point: snapped.world }],
        cursor: 'crosshair',
      };
    }
    return {
      snap: snapped,
      preview: [{ kind: 'polyline', points: [...this.state.points, snapped.world], closed: false }],
      cursor: 'crosshair',
    };
  }

  onKeyDown(context: ToolContext, key: string): ToolResponse | null {
    if (key !== 'Enter') return null;
    if (this.state.name !== 'collecting' || this.state.ids.length < 3) return null;
    return this.close(context);
  }

  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    return { preview: [] };
  }

  reset(): void {
    this.state = { name: 'idle' };
  }

  private close(context: ToolContext): ToolResponse {
    if (this.state.name !== 'collecting') return {};
    const id = context.nextId('poly');
    const entity = createEntity(id, { op: 'polygon', vertices: this.state.ids }, context.style);
    this.state = { name: 'idle' };
    return { actions: [{ kind: 'add-entity', entity }], selection: [id], preview: [] };
  }
}

/* ------------------------------------------------------------------- helpers */

export function createVectorTool(): Tool {
  return new TwoPointTool(
    'vector',
    'Vectơ',
    // Not 'v': that is the selection tool, by a convention older than this editor.
    'w',
    'vec',
    (from, to) => ({ op: 'vector', from, to }),
    (from, to) => ({ kind: 'vector', from, to })
  );
}

/**
 * Every tool, in toolbar order.
 *
 * Drawing tools first, then constructions. The order matters more than it looks: the
 * palette is the only place a user discovers what the editor can do, and burying "segment"
 * below nineteen constructions would make the common case the hardest to find.
 */
export function createDefaultTools(): readonly Tool[] {
  return [
    new PointTool(),
    createSegmentTool(),
    createRayTool(),
    createLineTool(),
    createVectorTool(),
    createCircleTool(),
    new PolygonTool(),
    ...createDrawingTools(),
    new TransformTool(),
    new TextTool(),
    ...createConstructionTools(),
    ...createFreePlaneTools(),
    ...createSolidTools(),
  ];
}
